import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fileUrl, formatRupiah } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { Search, ShoppingBag, Store, Plus, Minus, X, ArrowLeft, Bell } from "lucide-react";
import { toast } from "sonner";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function MenuBrowse() {
  const q = useQuery();
  const navigate = useNavigate();
  const { items: cartItems, addItem, subtotal, totalCount, tableNumber, setTableNumber } = useCart();

  const [settings, setSettings] = useState(null);
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [optionGroups, setOptionGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState(null);
  const [sheet, setSheet] = useState({ open: false, item: null });
  const [openStatus, setOpenStatus] = useState({ open: true });
  const [callOpen, setCallOpen] = useState(false);
  const [callSending, setCallSending] = useState(false);

  useEffect(() => {
    const tbl = q.get("table");
    if (tbl) setTableNumber(tbl);
  }, [q, setTableNumber]);

  useEffect(() => {
    (async () => {
      const [s, g, i, og, os] = await Promise.all([
        api.get("/settings"),
        api.get("/menu/groups"),
        api.get("/menu/items"),
        api.get("/option-groups"),
        api.get("/open-status"),
      ]);
      setSettings(s.data);
      setGroups(g.data);
      setItems(i.data);
      setOptionGroups(og.data);
      setOpenStatus(os.data);
      if (g.data[0]) setActiveGroup(g.data[0].id);
    })();
  }, []);

  const filtered = items.filter((it) => it.available !== false && (
    (!search || it.name.toLowerCase().includes(search.toLowerCase()))
  ));

  const groupRefs = useRef({});
  const scrollToGroup = (gid) => {
    setActiveGroup(gid);
    groupRefs.current[gid]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sendCall = async (type) => {
    if (!tableNumber) return toast.error("Nomor meja belum terisi");
    setCallSending(true);
    try {
      await api.post("/calls", { table_number: tableNumber, type });
      toast.success("Pelayan dipanggil, mohon tunggu sebentar 🙏");
      setCallOpen(false);
    } catch { toast.error("Gagal memanggil pelayan"); }
    finally { setCallSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6] pb-28">
      {!openStatus.open && (
        <div className="bg-[#D64045] text-white text-center py-2.5 px-4 text-sm font-medium sticky top-0 z-30" data-testid="closed-banner">
          {openStatus.next_message || "Tutup — pesan besok"}
        </div>
      )}      {/* Hero */}
      <div className="relative">
        <div className="h-40 md:h-56 bg-gradient-to-br from-[#1E3A2B] to-[#C25934]" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-white/90 flex items-center justify-center overflow-hidden shadow">
              {settings?.logo_path ? <img src={fileUrl(settings.logo_path)} alt="logo" className="w-full h-full object-cover" /> : <Store className="w-5 h-5 text-[#1E3A2B]" />}
            </div>
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold text-white" data-testid="customer-restaurant-name">{settings?.restaurant_name || "Warung Kasir"}</h1>
              <div className="text-white/80 text-xs">Meja <span className="font-semibold" data-testid="customer-table">{tableNumber || "-"}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(30,58,43,0.08)] border border-[#EAE7DF] p-3 flex items-center gap-2">
          <Search className="w-4 h-4 text-[#5C6A61] ml-2" />
          <input
            data-testid="input-search-menu"
            placeholder="Cari menu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 outline-none bg-transparent text-[#1E3A2B] placeholder:text-[#5C6A61]/60 py-2"
          />
        </div>

        {/* Sub-nav */}
        <div className="sticky top-0 z-20 bg-[#F9F9F6]/80 backdrop-blur-md py-3 mt-3 -mx-4 md:-mx-6 px-4 md:px-6 overflow-x-auto">
          <div className="flex gap-2">
            {groups.map((g) => (
              <button
                key={g.id}
                data-testid={`cat-${g.id}`}
                onClick={() => scrollToGroup(g.id)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-colors ${
                  activeGroup === g.id ? "bg-[#1E3A2B] text-white" : "bg-white text-[#1E3A2B] border border-[#D3CEC4]"
                }`}
              >
                {g.name}
              </button>
            ))}
          </div>
        </div>

        {/* Items grouped by group */}
        <div className="mt-4 space-y-6">
          {groups.length === 0 && (
            <div className="text-center py-16 text-[#5C6A61]">Menu belum tersedia. Silakan hubungi admin.</div>
          )}
          {groups.map((g) => {
            const groupItems = filtered.filter((it) => it.group_id === g.id);
            if (search && groupItems.length === 0) return null;
            return (
              <section key={g.id} ref={(el) => (groupRefs.current[g.id] = el)}>
                <h2 className="font-display text-xl font-semibold text-[#1E3A2B] mb-3">{g.name}</h2>
                <div className="grid gap-3">
                  {groupItems.length === 0 && <div className="text-sm text-[#5C6A61]">Belum ada menu di grup ini.</div>}
                  {groupItems.map((it) => (
                    <button
                      key={it.id}
                      data-testid={`menu-item-${it.id}`}
                      onClick={() => setSheet({ open: true, item: it })}
                      className="bg-white p-3 rounded-2xl shadow-[0_8px_32px_rgba(30,58,43,0.04)] border border-[#EAE7DF] hover:-translate-y-0.5 transition-transform flex gap-3 text-left"
                    >
                      <div className="w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden bg-[#EAE7DF]">
                        {it.image_path ? <img src={fileUrl(it.image_path)} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#EAE7DF]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1E3A2B]">{it.name}</div>
                        <div className="text-xs text-[#5C6A61] line-clamp-2 mt-0.5">{it.description}</div>
                        <div className="font-display font-semibold text-[#C25934] mt-2">{formatRupiah(it.price)}</div>
                      </div>
                      <div className="self-end">
                        <div className="w-9 h-9 rounded-full bg-[#C25934] text-white flex items-center justify-center shadow"><Plus className="w-4 h-4" /></div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      {/* Bottom cart bar */}
      {totalCount > 0 && (
        <button
          onClick={() => openStatus.open ? navigate("/cart") : null}
          disabled={!openStatus.open}
          data-testid="btn-view-cart"
          className={`fixed bottom-4 left-4 right-4 max-w-xl mx-auto px-5 py-4 rounded-2xl shadow-lg flex items-center justify-between slide-up transition-colors ${openStatus.open ? "bg-[#C25934] text-white hover:bg-[#A94726]" : "bg-[#5C6A61] text-white opacity-80"}`}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <ShoppingBag className="w-5 h-5" />
              <span className="absolute -top-2 -right-2 bg-white text-[#C25934] rounded-full text-[10px] font-bold w-5 h-5 flex items-center justify-center">{totalCount}</span>
            </div>
            <span className="font-medium">{openStatus.open ? "Lihat Keranjang" : "Toko sedang tutup"}</span>
          </div>
          <div className="font-display font-semibold">{formatRupiah(subtotal)}</div>
        </button>
      )}

      {/* Floating call-waiter button (above cart bar) */}
      {tableNumber && (
        <button
          onClick={() => setCallOpen(true)}
          data-testid="btn-call-waiter"
          className={`fixed right-4 ${totalCount > 0 ? "bottom-24" : "bottom-4"} max-w-xl mx-auto w-14 h-14 rounded-full bg-[#1E3A2B] text-white shadow-lg flex items-center justify-center hover:bg-[#2b5240] transition-colors z-30`}
          aria-label="Panggil Pelayan"
          title="Panggil Pelayan"
        >
          <Bell className="w-5 h-5" />
        </button>
      )}

      {callOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center" onClick={() => setCallOpen(false)} data-testid="call-waiter-sheet">
          <div className="bg-white w-full md:max-w-sm rounded-t-3xl md:rounded-3xl p-5 slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-[#1E3A2B]">Panggil Pelayan</h3>
              <button onClick={() => setCallOpen(false)} data-testid="btn-close-call"><X className="w-5 h-5 text-[#5C6A61]" /></button>
            </div>
            <p className="text-sm text-[#5C6A61] mb-4">Pelayan akan segera menghampiri Meja {tableNumber}.</p>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="btn-call-waiter-general" disabled={callSending} onClick={() => sendCall("waiter")} className="bg-[#C25934] text-white py-3 rounded-xl font-medium disabled:opacity-50">Pelayan</button>
              <button data-testid="btn-call-water" disabled={callSending} onClick={() => sendCall("water")} className="bg-[#1E3A2B] text-white py-3 rounded-xl font-medium disabled:opacity-50">Minta Air</button>
              <button data-testid="btn-call-bill" disabled={callSending} onClick={() => sendCall("bill")} className="bg-[#D4A373] text-[#1E3A2B] py-3 rounded-xl font-medium disabled:opacity-50">Minta Bon</button>
              <button data-testid="btn-call-other" disabled={callSending} onClick={() => sendCall("other")} className="bg-[#EAE7DF] text-[#1E3A2B] py-3 rounded-xl font-medium disabled:opacity-50">Lainnya</button>
            </div>
          </div>
        </div>
      )}

      <ItemSheet
        open={sheet.open}
        item={sheet.item}
        optionGroups={optionGroups}
        onClose={() => setSheet({ open: false, item: null })}
        onAdd={(cartItem) => { addItem(cartItem); setSheet({ open: false, item: null }); }}
        onSelectRelated={(relItem) => setSheet({ open: true, item: relItem })}
      />
    </div>
  );
}

function ItemSheet({ open, item, optionGroups, onClose, onAdd, onSelectRelated }) {
  const [qty, setQty] = useState(1);
  const [selections, setSelections] = useState({}); // group_id -> array of option objs
  const [notes, setNotes] = useState("");
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (open && item) {
      setQty(1); setSelections({}); setNotes("");
      api.get(`/menu/items/${item.id}/related?limit=4`).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
    }
  }, [open, item]);

  if (!open || !item) return null;

  const linkedGroups = optionGroups.filter((og) => (item.option_group_ids || []).includes(og.id));

  const toggleOption = (og, opt) => {
    const current = selections[og.id] || [];
    const exists = current.find((x) => x.option_id === opt.id);
    let next;
    if (exists) next = current.filter((x) => x.option_id !== opt.id);
    else if (og.max_select === 1) next = [{ group_id: og.id, option_id: opt.id, name: opt.name, price_delta: opt.price_delta }];
    else if (current.length < og.max_select) next = [...current, { group_id: og.id, option_id: opt.id, name: opt.name, price_delta: opt.price_delta }];
    else next = current;
    setSelections({ ...selections, [og.id]: next });
  };

  const allValid = linkedGroups.every((og) => (selections[og.id]?.length || 0) >= og.min_select);
  const optSum = Object.values(selections).flat().reduce((a, o) => a + o.price_delta, 0);
  const total = (item.price + optSum) * qty;

  const handleAdd = () => {
    if (!allValid) return;
    onAdd({
      menu_item_id: item.id,
      name: item.name,
      unit_price: item.price,
      quantity: qty,
      options: Object.values(selections).flat(),
      notes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center" onClick={onClose} data-testid="item-sheet">
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl max-h-[90vh] overflow-y-auto slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          {item.image_path ? (
            <img src={fileUrl(item.image_path)} alt={item.name} className="w-full aspect-video object-cover" />
          ) : <div className="w-full aspect-video bg-[#EAE7DF]" />}
          <button data-testid="btn-close-sheet" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow">
            <X className="w-4 h-4 text-[#1E3A2B]" />
          </button>
        </div>
        <div className="p-5">
          <h3 className="font-display text-xl font-semibold text-[#1E3A2B]">{item.name}</h3>
          <div className="text-sm text-[#5C6A61] mt-1">{item.description}</div>
          <div className="font-display font-semibold text-[#C25934] text-lg mt-2">{formatRupiah(item.price)}</div>

          {linkedGroups.map((og) => (
            <div key={og.id} className="mt-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-[#1E3A2B]">{og.name}</div>
                <div className="text-xs text-[#5C6A61]">{og.min_select > 0 ? "Wajib" : "Opsional"} · max {og.max_select}</div>
              </div>
              <div className="space-y-1.5 mt-2">
                {og.options.map((opt) => {
                  const sel = (selections[og.id] || []).some((x) => x.option_id === opt.id);
                  return (
                    <button
                      type="button"
                      key={opt.id}
                      data-testid={`opt-${opt.id}`}
                      onClick={() => toggleOption(og, opt)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-xl text-sm transition-colors ${sel ? "border-[#C25934] bg-[#C25934]/5 text-[#1E3A2B]" : "border-[#D3CEC4] text-[#1E3A2B]"}`}
                    >
                      <span>{opt.name}</span>
                      <span className="text-[#5C6A61]">{opt.price_delta > 0 ? `+${formatRupiah(opt.price_delta)}` : (opt.price_delta < 0 ? formatRupiah(opt.price_delta) : "")}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-5">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Catatan</span>
              <textarea data-testid="input-item-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Contoh: tidak pedas, tanpa bawang..." className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5 text-sm" />
            </label>
          </div>

          {related.length > 0 && (
            <div className="mt-5" data-testid="related-items">
              <div className="text-xs uppercase tracking-widest font-bold text-[#5C6A61] mb-2 flex items-center gap-1">
                <span>✨</span> Sering dipesan bersama
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1 -mx-5 px-5">
                {related.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`related-${r.id}`}
                    onClick={() => onSelectRelated && onSelectRelated(r)}
                    className="flex-shrink-0 w-32 bg-white border border-[#EAE7DF] rounded-xl p-2 text-left hover:border-[#C25934] transition-colors"
                  >
                    <div className="w-full aspect-square rounded-lg overflow-hidden bg-[#EAE7DF] mb-2">
                      {r.image_path ? <img src={fileUrl(r.image_path)} alt={r.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#EAE7DF]" />}
                    </div>
                    <div className="text-xs font-medium text-[#1E3A2B] line-clamp-2">{r.name}</div>
                    <div className="text-xs font-display font-semibold text-[#C25934] mt-1">{formatRupiah(r.price)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#EAE7DF] p-4 flex items-center gap-3">
          <div className="flex items-center gap-2 border border-[#D3CEC4] rounded-full px-1">
            <button data-testid="btn-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 flex items-center justify-center text-[#1E3A2B]"><Minus className="w-4 h-4" /></button>
            <span className="w-6 text-center font-medium" data-testid="item-qty">{qty}</span>
            <button data-testid="btn-qty-plus" onClick={() => setQty((q) => q + 1)} className="w-9 h-9 flex items-center justify-center text-[#1E3A2B]"><Plus className="w-4 h-4" /></button>
          </div>
          <button
            data-testid="btn-add-to-cart"
            disabled={!allValid}
            onClick={handleAdd}
            className="flex-1 bg-[#C25934] text-white hover:bg-[#A94726] py-3 rounded-xl font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <span>Tambahkan</span>
            <span className="opacity-90">· {formatRupiah(total)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
